import axios, { AxiosHeaders } from 'axios'
import { getBrandIdentifier } from 'branding/branding.store'
import config from 'config'

import { getAuthorizationSessionHeader } from './utils/getAuthorizationSessionHeader'
import { getCookie } from './utils/getCookie'

export function defaultLosJsonHeaders() {
    // Fallback auth for iframe contexts where SameSite=Lax blocks session
    // cookies cross-origin. Backend accepts this via SessionAuthorizationHeaderMiddleware.
    const sessionHeader = getAuthorizationSessionHeader()
    const brandIdentifier = getBrandIdentifier()

    return {
        'Content-Type': 'application/json',
        // The current version of the dealer web app
        'X-Octane-Client-Version': process.env.REACT_APP_RELEASE_VERSION,
        'X-Octane-Client-Name': process.env.REACT_APP_NAME,
        'X-Hash-Path': window.location.hash,
        ...(sessionHeader ? { Authorization: sessionHeader } : {}),
        ...(brandIdentifier ? { 'Branding-Identifier': brandIdentifier } : {}),
    }
}

function getTargetUrl(req: { url?: string }) {
    const url = req.url ?? ''
    const isAbsolute = /^https?:\/\//i.test(url)

    return isAbsolute ? url : `${config.API_BASE_URL}${url}`
}

export function setCsrfCookieInHeaders() {
    axios.interceptors.request.use(
        (req) => {
            try {
                const target = getTargetUrl(req)

                if (target.startsWith(config.API_BASE_URL)) {
                    const headers = AxiosHeaders.from(req.headers)
                    const token = getCookie('csrftoken')

                    if (token && !headers.has('X-CSRFToken')) {
                        headers.set('X-CSRFToken', token)
                    }

                    req.headers = headers
                    req.withCredentials = true
                }

                return req
            } catch (error) {
                // Log error but don't block the request
                // This ensures the portal remains functional even if CSRF token addition fails
                // eslint-disable-next-line no-console
                console.error('CSRF interceptor error:', error)

                return req
            }
        },
        (error) => {
            // Handle errors in interceptor registration
            return Promise.reject(error)
        },
    )
}

export function setBrandIdentifierInHeaders() {
    // This interceptor adds the 'Branding-Identifier' header to all outgoing API requests if it's not already present.
    axios.interceptors.request.use(
        (req) => {
            try {
                const target = getTargetUrl(req)

                if (target.startsWith(config.API_BASE_URL)) {
                    const headers = AxiosHeaders.from(req.headers)
                    const brandIdentifier = getBrandIdentifier()

                    if (
                        brandIdentifier &&
                        !headers.has('Branding-Identifier')
                    ) {
                        headers.set('Branding-Identifier', brandIdentifier)
                    }

                    req.headers = headers
                }

                return req
            } catch (error) {
                // Log error but don't block the request
                // eslint-disable-next-line no-console
                console.error('Brand identifier interceptor error:', error)

                return req
            }
        },
        (error) => {
            // Handle errors in interceptor registration
            return Promise.reject(error)
        },
    )
}
